<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/50" @click="$emit('close')" />

    <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-start justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <div class="flex-1 min-w-0 pr-4">
          <div class="flex items-center gap-2 mb-1">
            <span
              class="text-xs px-2 py-0.5 rounded-full font-medium"
              :class="priorityClass"
            >
              {{ priorityLabel }}
            </span>
            <span class="text-xs text-slate-400">{{ stageName }}</span>
          </div>
          <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100 leading-snug">
            {{ card.title }}
          </h2>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <button
            class="px-3 py-1.5 text-xs bg-woot-50 hover:bg-woot-100 text-woot-600 rounded-lg transition-colors"
            @click="$emit('edit', card)"
          >
            <span class="i-lucide-pencil size-3 mr-1" />
            Editar
          </button>
          <button
            class="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600"
            @click="$emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto">
        <div class="grid grid-cols-3 gap-0 h-full">
          <!-- Main content -->
          <div class="col-span-2 px-6 py-4 space-y-4 border-r border-slate-200 dark:border-slate-700">
            <!-- Description -->
            <div v-if="card.description">
              <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Descrição</h3>
              <p class="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{{ card.description }}</p>
            </div>

            <!-- Linked conversation -->
            <div v-if="card.conversation">
              <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Conversa vinculada</h3>
              <div class="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
                <span class="i-lucide-message-circle size-5 text-woot-500" />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-medium text-woot-600">#{{ card.conversation.display_id }}</span>
                    <span class="text-xs px-1.5 py-0.5 rounded-full" :class="conversationStatusClass">
                      {{ conversationStatusLabel }}
                    </span>
                  </div>
                  <p v-if="card.conversation.meta?.sender?.name" class="text-xs text-slate-500 truncate">
                    {{ card.conversation.meta?.sender?.name }}
                  </p>
                </div>
                <button
                  class="text-xs text-woot-500 hover:text-woot-600 hover:underline"
                  @click="openConversation"
                >
                  Abrir
                </button>
              </div>
            </div>

            <!-- Activity / notes placeholder -->
            <div>
              <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Atividade</h3>
              <div class="text-sm text-slate-400 italic">Nenhuma atividade registrada.</div>
            </div>
          </div>

          <!-- Sidebar info -->
          <div class="px-4 py-4 space-y-4">
            <!-- Contact -->
            <div v-if="card.contact">
              <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Contato</h3>
              <div class="flex items-center gap-2">
                <img
                  v-if="card.contact.thumbnail"
                  :src="card.contact.thumbnail"
                  class="size-8 rounded-full object-cover"
                />
                <span v-else class="size-8 rounded-full bg-woot-500 flex items-center justify-center text-white text-sm font-bold">
                  {{ card.contact.name?.charAt(0)?.toUpperCase() }}
                </span>
                <div>
                  <p class="text-sm font-medium text-slate-700 dark:text-slate-200">{{ card.contact.name }}</p>
                  <p v-if="card.contact.phone_number" class="text-xs text-slate-500">{{ card.contact.phone_number }}</p>
                </div>
              </div>
            </div>

            <!-- Assignee -->
            <div v-if="card.assignee">
              <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Responsável</h3>
              <div class="flex items-center gap-2">
                <img
                  v-if="card.assignee.thumbnail"
                  :src="card.assignee.thumbnail"
                  class="size-6 rounded-full object-cover"
                />
                <span v-else class="size-6 rounded-full bg-slate-400 flex items-center justify-center text-white text-xs font-bold">
                  {{ card.assignee.name?.charAt(0)?.toUpperCase() }}
                </span>
                <span class="text-sm text-slate-700 dark:text-slate-200">{{ card.assignee.name }}</span>
              </div>
            </div>

            <!-- Deal value -->
            <div>
              <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Valor</h3>
              <p class="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {{ formatCurrency(card.deal_value, card.currency) }}
              </p>
            </div>

            <!-- Due date -->
            <div v-if="card.due_date">
              <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Vencimento</h3>
              <p class="text-sm" :class="dueDateClass">{{ formatDate(card.due_date) }}</p>
            </div>

            <!-- Created at -->
            <div>
              <h3 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Criado em</h3>
              <p class="text-xs text-slate-500">{{ formatDate(card.created_at) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps({
  card: { type: Object, required: true },
  stages: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'edit']);
const router = useRouter();

const stageName = computed(() => {
  const stage = props.stages.find(s => s.id === props.card.stage_id);
  return stage?.name || '';
});

const priorityClass = computed(() => ({
  'bg-red-100 text-red-700': props.card.priority === 'urgent',
  'bg-orange-100 text-orange-700': props.card.priority === 'high',
  'bg-yellow-100 text-yellow-700': props.card.priority === 'medium',
  'bg-slate-100 text-slate-600': props.card.priority === 'low',
}));

const priorityLabel = computed(() => ({
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
}[props.card.priority] || 'Baixa'));

const conversationStatusClass = computed(() => ({
  'bg-green-100 text-green-700': props.card.conversation?.status === 'open',
  'bg-slate-100 text-slate-600': props.card.conversation?.status === 'resolved',
  'bg-yellow-100 text-yellow-700': props.card.conversation?.status === 'pending',
}));

const conversationStatusLabel = computed(() => ({
  open: 'Aberta',
  resolved: 'Resolvida',
  pending: 'Pendente',
  snoozed: 'Adiada',
}[props.card.conversation?.status] || ''));

const dueDateClass = computed(() => {
  if (!props.card.due_date) return '';
  const due = new Date(props.card.due_date);
  const now = new Date();
  return due < now ? 'text-red-500 font-medium' : 'text-slate-700 dark:text-slate-200';
});

function formatCurrency(value, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function openConversation() {
  if (props.card.conversation) {
    const accountId = window.location.pathname.split('/')[3];
    router.push(`/app/accounts/${accountId}/conversations/${props.card.conversation.id}`);
    emit('close');
  }
}
</script>
